import React, { useMemo, useCallback, useState } from 'react';
import { Microscope } from 'lucide-react';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { ResearchProvider, useResearchContext } from '@/contexts/research/ResearchContext';
import { useResearchSubmission } from '@/components/research-generator/ResearchAssistant/hooks/useResearchSubmission';

// Reuse existing research components
import ResearchLightboxUniversal from '@/components/research-generator/ResearchLightboxUniversal';
import ResearchAssistant from '@/components/research-generator/ResearchAssistant';
import ResearchGenerationModal from '@/components/research-generator/ResearchGenerationModal';

// Import the universal empty state system (like Image Generator)
import { UniversalEmptyStateWrapper, EmptyStateLayout, getAppConfig, SimpleResearchForm } from '@/components/shared/empty-states';
// Import quest completion triggers
import { QuestCompletionTrigger, useQuestCompletionTrigger } from '@/components/onboarding';

// New modular components and hooks
import { PrivateResearchMainLayout } from '@/components/private-research-generator';
import { 
  usePrivateResearchLibraryData, 
  usePrivateResearchGeneration,
  usePrivateResearchNavigation,
  usePrivateResearchLightbox,
  usePrivateResearchDelete,
  usePrivateResearchDataTransform,
  type PrivateResearchItem,
} from '@/hooks/private-research-generator';

// Inner component that uses the context
const PrivateResearchGeneratorContent: React.FC = () => {
  const { isMobile } = useIsMobile();
  const { state } = useResearchContext();
  
  // Project selection state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  // Quest completion trigger
  const { triggerResearchGenerated } = useQuestCompletionTrigger();

  // Use existing workbook query to get all projects
  const { data: workbookData } = useWorkbookQuery({ 
    filterByStatus: 'all' // Get all non-archived projects
  });
  
  // Find selected project from the already-loaded projects data
  const selectedProject = useMemo(() => {
    if (!selectedProjectId || !workbookData?.projects) return null;
    return workbookData.projects.find(project => project.id === selectedProjectId);
  }, [selectedProjectId, workbookData?.projects]);

  // Use simple private research data with project filtering
  console.log('[PrivateResearchGenerator] About to call usePrivateResearchLibraryData with:', {
    selectedProjectId,
    projectIdType: typeof selectedProjectId,
    timestamp: new Date().toISOString()
  });
  
  const {
    research: allResearch,
    totalUserResearch, // NEW: Total user research for hasData calculation (like Image Generator)
    isLoading,
    isError: error,
    loadMore: fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    handleDeleteResearch,
    isDeleting,
    searchTerm,
    totalCount
  } = usePrivateResearchLibraryData(
    selectedProjectId ? { projectId: selectedProjectId } : {} // Only pass projectId if a project is selected
  );

  // NEW: Use total user research for hasData (like Image Generator pattern)
  const hasData = totalUserResearch.length > 0;

  // Get app configuration (like Text and Image generators)
  const appConfig = getAppConfig('private-research-generator');
  
  // Dynamic title based on project selection
  const dynamicTitle = selectedProject?.title || appConfig.title;

  const generateResearchMutation = usePrivateResearchGeneration();

  // Modal state for research generation (keep for empty state form)
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [researchParams, setResearchParams] = useState<any>(null);

  // Lightbox management
  const {
    selectedResearch,
    isLightboxOpen,
    isGeneratingRelatedQuestion,
    currentIndex,
    hasPrevious,
    hasNext,
    handleResearchClick,
    handleCloseLightbox,
    handlePreviousResearch,
    handleNextResearch,
    handleGenerationStateChange,
    handleToggleFavorite,
  } = usePrivateResearchLightbox({ allResearch });

  // Navigation with URL handling
  const { handleCloseLightboxWithUrlCleanup } = usePrivateResearchNavigation({
    allResearch,
    isLightboxOpen,
    onResearchClick: handleResearchClick,
  });

  // Data transformation
  const {
    transformedResearch,
    transformedSelectedResearch,
    selectedResearchDetails,
    isLoadingDetails,
  } = usePrivateResearchDataTransform({
    allResearch,
    selectedResearch,
  });

  // Delete operations
  const {
    showDeleteConfirmation,
    handleDeleteResearchClick,
    confirmDeleteResearch,
    cancelDeleteResearch,
  } = usePrivateResearchDelete({
    selectedResearch,
    handleDeleteResearch,
    isDeleting,
    onDeleteComplete: handleCloseLightboxWithUrlCleanup,
  });

  // Handle project change with query invalidation
  const handleProjectChange = (projectId: string | null) => {
    console.log('[PrivateResearchGenerator] Project selection changed:', {
      from: selectedProjectId,
      to: projectId,
      timestamp: new Date().toISOString()
    });
    setSelectedProjectId(projectId);
  };

  // Research generation handlers
  const handleGenerateResearch = useCallback(() => {
    console.log('[PrivateResearchGenerator] Generate research triggered from ResearchAssistant - showing modal');
    
    // Prepare research parameters for modal using the state from context
    // Filter out "none" values to prevent API errors
    const params = {
      researchType: state.selectedType,
      researchPrompt: state.researchPrompt,
      timeframe: state.timeframe && state.timeframe !== 'none' ? state.timeframe : undefined,
      country: state.country && state.country !== 'none' ? state.country : undefined,
      sourceType: state.sourceType && state.sourceType !== 'none' ? state.sourceType : undefined,
      // selectedFilters removed - no longer sending dynamic filters
      workbook_id: selectedProjectId || null,
    };
    
    // Show the generation modal
    setResearchParams(params);
    setShowGenerationModal(true);
  }, [selectedProjectId, state]);

  // Simple research generation for empty state form
  const handleSimpleResearchGeneration = useCallback((prompt: string) => {
    console.log('Simple research generation triggered from empty state form');
    
    // Prepare research parameters and show modal
    const params = {
      researchType: null, // No specific type for simple generation
      researchPrompt: prompt,
      timeframe: undefined,
      country: undefined, 
      sourceType: undefined,
      // selectedFilters removed - no longer sending dynamic filters
      workbook_id: selectedProjectId, // Direct project assignment via workbook_id
    };
    
    setResearchParams(params);
    setShowGenerationModal(true);
  }, [selectedProjectId]);

  // Create a dummy mutation that doesn't actually do anything
  // This prevents the ResearchAssistant from calling the real mutation
  const dummyMutation = {
    mutateAsync: async () => {
      // Do nothing - the modal will handle the actual generation
      return null;
    },
    isPending: false,
  } as any;

  const handleResearchGenerated = useCallback((research: any) => {
    console.log('[PrivateResearchGenerator] Private research generated via ResearchAssistant:', research);
    
    // Trigger quest completion
    triggerResearchGenerated();
    
    // Open the research in lightbox
    if (research) {
      handleResearchClick(research);
    }
  }, [triggerResearchGenerated, handleResearchClick]);
  
  // Memoize the sidebar content to prevent re-initialization during layout changes
  const sidebarContent = useMemo(() => (
    <ResearchAssistant 
      onGenerateResearch={handleGenerateResearch} 
      onResearchGenerated={handleResearchGenerated}
      generateResearchMutation={generateResearchMutation} // Use REAL mutation, not dummy
      selectedProjectId={selectedProjectId}
      onProjectChange={handleProjectChange}
    />
  ), [handleGenerateResearch, handleResearchGenerated, generateResearchMutation, selectedProjectId, handleProjectChange]);

  // Enhanced close handler that combines lightbox close with URL cleanup
  const handleLightboxClose = useCallback(() => {
    handleCloseLightbox();
    handleCloseLightboxWithUrlCleanup();
  }, [handleCloseLightbox, handleCloseLightboxWithUrlCleanup]);

  // Handle generation modal close
  const handleGenerationModalClose = () => {
    setShowGenerationModal(false);
    setResearchParams(null);
  };

  // Handle generation success
  const handleGenerationSuccess = (research: any) => {
    console.log('Research generation successful, opening lightbox:', research);
    
    // Trigger quest completion
    triggerResearchGenerated();
    
    handleGenerationModalClose();
    
    // Trigger the callback that opens the lightbox
    if (research) {
      handleResearchClick(research);
    }
  };

  // Empty state content (matching Text and Image Generator pattern)
  const emptyStateContent = (
    <EmptyStateLayout
      icon={<appConfig.icon className={`h-8 w-8 ${appConfig.iconColor}`} />}
      title={appConfig.emptyStateTitle}
      variant={appConfig.variant}
    >
      <SimpleResearchForm 
        onGenerateResearch={handleSimpleResearchGeneration}
        onResearchGenerated={() => {
          // Research generation will automatically refresh the research list
          console.log('Research generated, layout will update automatically');
          // Trigger quest completion for empty state generation
          triggerResearchGenerated();
        }}
      />
    </EmptyStateLayout>
  );

  return (
    <>
      {/* Quest completion trigger for research generation */}
      <QuestCompletionTrigger 
        stepId={2} 
        action="research_generated_from_research_generator" 
      />
      
      {/* NEW: Use UniversalEmptyStateWrapper pattern like Image Generator */}
      <UniversalEmptyStateWrapper
        isLoading={isLoading}
        hasData={hasData} // Based on total user research, not filtered results
        title={appConfig.title}
        description={appConfig.description}
        icon={<appConfig.icon size={24} />}
        sidebar={sidebarContent}
        emptyStateContent={emptyStateContent}
      >
        {/* Normal content when research exists - filtered results */}
        <PrivateResearchMainLayout
          allResearch={allResearch} // Filtered results
          transformedResearch={transformedResearch}
          isLoading={isLoading}
          error={error}
          searchTerm={searchTerm}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onResearchClick={handleResearchClick}
          onToggleFavorite={handleToggleFavorite}
          onLoadMore={fetchNextPage}
          title={dynamicTitle}
          activeFilters={{
            researchType: state.selectedType,
            timeframe: state.timeframe,
            country: state.country,
            sourceType: state.sourceType,
            projectName: selectedProject?.title
            // Dynamic filters removed - no longer displaying type-specific filters
          }}
        />
      </UniversalEmptyStateWrapper>

        {/* Research Lightbox - Only render when needed */}
        {selectedResearch && (
          <ResearchLightboxUniversal
            research={transformedSelectedResearch}
            allResearch={transformedResearch}
            isOpen={isLightboxOpen}
          onClose={handleLightboxClose}
            isLoading={isLoadingDetails}
            researchDetails={selectedResearchDetails || selectedResearch}
            onRelatedQuestionClick={handleResearchClick}
            // Navigation props
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            isLoadingMore={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onPrevious={handlePreviousResearch}
            onNext={handleNextResearch}
            onLoadMore={fetchNextPage}
            // Generation state
            isGeneratingRelatedQuestion={isGeneratingRelatedQuestion}
            onGenerationStateChange={handleGenerationStateChange}
            // Delete functionality
            isOwner={true} // Always owner for private research
            isDeleteLoading={isDeleting}
            onDelete={handleDeleteResearchClick}
            showFavoriteButton={false}
            contentType="private_research"
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirmation}
          onConfirm={confirmDeleteResearch}
          onCancel={cancelDeleteResearch}
          title="Delete Private Research"
          description={`Are you sure you want to delete "${selectedResearch?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isConfirmLoading={isDeleting}
          confirmVariant="red"
        />
        
        {/* Research Generation Modal - Beautiful Progress */}
        <ResearchGenerationModal
          isOpen={showGenerationModal}
          onClose={handleGenerationModalClose}
          researchParams={researchParams}
          researchMutation={generateResearchMutation}
          onSuccess={handleGenerationSuccess}
        />
    </>
  );
};

// Main container component that provides the context
const PrivateResearchGenerator: React.FC = () => {
  return (
    <ResearchProvider>
      <PrivateResearchGeneratorContent />
    </ResearchProvider>
  );
};

export default PrivateResearchGenerator; 